import React, { useRef } from "react";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { HeartButton } from "@/components/HeartButton";
import { API } from "@/lib/api";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ExternalLink, SkipForward as Fwd, ShoppingBag, Loader2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const EQ_BARS = [0, 1, 2, 3, 4, 5, 6];
const Equalizer = ({ playing }) => (
  <div className="flex items-end gap-[3px] h-5 shrink-0" data-testid="player-waveform" aria-hidden="true">
    {EQ_BARS.map((i) => (
      <span
        key={i}
        className="eq-bar w-[3px] rounded-full bg-[#e2ff31]"
        style={{
          animationDelay: `${i * 0.12}s`,
          animationPlayState: playing ? "running" : "paused",
          height: playing ? undefined : "18%",
        }}
      />
    ))}
  </div>
);

const WaveformSeek = ({ peaks, progress, duration, onSeek }) => {
  const ref = useRef(null);
  const pct = duration ? Math.min(1, progress / duration) : 0;
  const handleSeek = (e) => {
    if (!ref.current || !duration) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(1, x)) * duration);
  };
  return (
    <div ref={ref} data-testid="player-waveform-seek" onClick={handleSeek}
      className="flex-1 flex items-center gap-[1.5px] h-8 cursor-pointer" role="slider" aria-label="Seek">
      {peaks.map((p, i) => {
        const played = (i + 0.5) / peaks.length <= pct;
        return (
          <span key={i} className="flex-1 rounded-full transition-colors duration-150"
            style={{ height: `${Math.max(8, p * 100)}%`, backgroundColor: played ? "#e2ff31" : "#3a3a3a" }} />
        );
      })}
    </div>
  );
};

export const AudioPlayer = () => {
  const { current, ad, adCountdown, skipAd, clickAd, isPlaying, progress, duration, volume, togglePlay, seek, setVolume, next, prev } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  const lastVolRef = useRef(0.8);
  const muted = volume === 0;
  const toggleMute = () => {
    if (muted) {
      setVolume(lastVolRef.current || 0.8);
    } else {
      lastVolRef.current = volume;
      setVolume(0);
    }
  };

  if (!current && !ad) return null;

  // ----- Ad mode -----
  if (ad) {
    return (
      <div data-testid="audio-player-ad" className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#0a0a0a]/90 border-t border-[#e2ff31]/40">
        <div className="px-4 md:px-8 py-3 flex items-center gap-4">
          <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-black bg-[#e2ff31] rounded-full px-2.5 py-1 font-bold">
            Ad
          </span>
          <button data-testid="player-toggle" onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-[#e2ff31] text-black flex items-center justify-center hover:bg-[#c8e619] active:scale-95 transition-all shrink-0">
            {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
          </button>
          <button onClick={clickAd} data-testid="ad-click" className="min-w-0 flex-1 text-left group">
            <p className="font-medium text-sm truncate group-hover:text-[#e2ff31] transition-colors flex items-center gap-1">
              {ad.title || "Sponsored message"} <ExternalLink className="w-3 h-3 opacity-60" />
            </p>
            <p className="text-xs text-[#9ca3af] truncate">Advertisement — supports this artist</p>
          </button>
          <button data-testid="ad-skip-btn" onClick={skipAd} disabled={adCountdown > 0}
            className="shrink-0 flex items-center gap-1.5 text-sm rounded-full border border-[#2a2a2a] px-4 py-2 disabled:opacity-50 enabled:hover:bg-[#1e1e1e] transition-colors">
            {adCountdown > 0 ? `Skip in ${adCountdown}s` : <>Skip ad <Fwd className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    );
  }

  // ----- Track mode -----
  return (
    <div data-testid="audio-player" className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#050505]/80 border-t border-white/10">
      <div className="px-4 md:px-8 py-3 flex items-center gap-4">
        <div className="flex items-center gap-3 w-1/4 min-w-0">
          <img key={current.id} src={`${API}${current.cover_url}`} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            className="w-12 h-12 rounded-lg object-cover border border-[#2a2a2a] bg-[#1e1e1e] shrink-0" />
          <div className="min-w-0 hidden sm:block">
            <p className="font-medium text-sm truncate" data-testid="player-track-title">{current.title}</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-[#9ca3af] truncate">{current.producer}</p>
              <Equalizer playing={isPlaying} />
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center gap-1">
          <div className="flex items-center gap-4">
            <button data-testid="player-prev" onClick={prev} className="text-[#9ca3af] hover:text-white transition-colors">
              <SkipBack className="w-5 h-5" />
            </button>
            <button data-testid="player-toggle" onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-[#e2ff31] text-black flex items-center justify-center hover:bg-[#c8e619] active:scale-95 transition-all">
              {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
            </button>
            <button data-testid="player-next" onClick={next} className="text-[#9ca3af] hover:text-white transition-colors">
              <SkipForward className="w-5 h-5" />
            </button>
          </div>
          <div className="w-full flex items-center gap-2 max-w-xl">
            <span className="text-[10px] font-mono text-[#6b7280] w-9 text-right">{fmt(progress)}</span>
            {current.waveform && current.waveform.length ? (
              <WaveformSeek peaks={current.waveform} progress={progress} duration={duration} onSeek={seek} />
            ) : (
              <Slider data-testid="player-seek" value={[progress]} max={duration || 100} step={0.5}
                onValueChange={(v) => seek(v[0])} className="flex-1" />
            )}
            <span className="text-[10px] font-mono text-[#6b7280] w-9">{fmt(duration)}</span>
          </div>
        </div>

        <div className="w-auto md:w-1/4 flex items-center justify-end gap-3">
          {current.sold ? (
            <span data-testid="player-sold" className="text-xs font-bold text-[#ef4444] border border-[#ef4444]/40 rounded-full px-3 py-1.5 uppercase tracking-wide">Sold</span>
          ) : (
            <>
              <HeartButton track={current} size="lg" />
              <button data-testid="player-buy" onClick={() => openBuy(current)} disabled={loadingId === current.id}
                className="flex items-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold text-sm px-4 py-2 shrink-0">
                {loadingId === current.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                ${current.price?.toFixed(2)}
              </button>
            </>
          )}
          <div className="hidden md:flex items-center gap-2">
            <button data-testid="player-mute" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
              className="text-[#9ca3af] hover:text-[#e2ff31] transition-colors">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <Slider data-testid="player-volume" value={[volume]} max={1} step={0.05} onValueChange={(v) => setVolume(v[0])} className="w-24" />
          </div>
        </div>
      </div>
    </div>
  );
};
